import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { useLocale } from './useLocale';
import { DEFAULT_LOCALE } from './locales';

const sessionCache = new Map<string, string>();

export function useDynamicTranslation(texts: string[]): {
  translated: string[];
  loading: boolean;
  error: Error | null;
  getTranslated: (text: string) => string;
} {
  const { locale } = useLocale();
  const [translated, setTranslated] = useState<string[]>(texts);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const textsRef = useRef(texts);
  textsRef.current = texts;

  const textsKey = useMemo(() => JSON.stringify(texts), [texts]);

  useEffect(() => {
    const currentTexts = textsRef.current;

    if (locale === DEFAULT_LOCALE) {
      setTranslated(currentTexts);
      setLoading(false);
      setError(null);
      return;
    }
    if (currentTexts.length === 0) {
      setTranslated([]);
      return;
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const result = new Array<string>(currentTexts.length);
    const missingTexts: string[] = [];
    const missingIndexes: number[] = [];

    currentTexts.forEach((text, i) => {
      const key = `${locale}:${text}`;
      const hit = sessionCache.get(key);
      if (hit !== undefined) {
        result[i] = hit;
      } else {
        missingTexts.push(text);
        missingIndexes.push(i);
      }
    });

    if (missingTexts.length === 0) {
      setTranslated(result);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    api
      .post(
        '/i18n/translate',
        { texts: missingTexts, target: locale, source: 'ko' },
        { signal: ctrl.signal },
      )
      .then((res) => {
        const translations: string[] = res.data.data.translations;
        translations.forEach((tr, i) => {
          const origText = missingTexts[i];
          sessionCache.set(`${locale}:${origText}`, tr);
          result[missingIndexes[i]] = tr;
        });
        if (!ctrl.signal.aborted) {
          setTranslated([...result]);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (ctrl.signal.aborted) return;
        // 실패 시 원문 fallback
        missingIndexes.forEach((origIdx, i) => {
          result[origIdx] = missingTexts[i];
        });
        setTranslated([...result]);
        setLoading(false);
        setError(err);
      });

    return () => ctrl.abort();
  }, [locale, textsKey]);

  const getTranslated = useMemo(() => {
    const map = new Map<string, string>();
    texts.forEach((text, i) => {
      if (!map.has(text)) map.set(text, translated[i] ?? text);
    });
    return (text: string) => map.get(text) ?? text;
  }, [texts, translated]);

  return { translated, loading, error, getTranslated };
}
